import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Shield, AlertTriangle, Settings, Zap, ExternalLink, Play, Cpu, Gauge } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../lib/api';
import { toast } from 'sonner';

const CreditScoreConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editTier, setEditTier] = useState(null);
  const [showFormula, setShowFormula] = useState(false);
  const [formulas, setFormulas] = useState(null);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, formulaRes] = await Promise.all([
        dashboardApi.creditScoreConfig.get(),
        dashboardApi.interestRateFormula(),
      ]);
      setConfig(cfgRes.data);
      setFormulas(formulaRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dashboardApi.creditScoreConfig.update(config);
      toast.success('Credit score configuration saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleRunNow = async () => {
    if (!window.confirm('Run the credit scoring engine now across all active clients? This will update PD/LGD overrides on their active loans.')) return;
    setRunning(true);
    try {
      const { data } = await dashboardApi.creditScoreConfig.runNow();
      setLastRun(data);
      toast.success(`Scored ${data.clients_scored}/${data.clients_processed} clients — ${data.loans_updated} loans updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run credit scoring');
    }
    setRunning(false);
  };

  const updateTier = (index, field, value) => {
    const updated = [...config.tiers];
    updated[index] = { ...updated[index], [field]: value };
    setConfig({ ...config, tiers: updated });
  };

  if (loading) return <div className="p-6 text-center py-16 text-gray-400">Loading configuration...</div>;
  if (!config) return <div className="p-6 text-center py-16 text-red-500">Failed to load configuration</div>;

  const tierColors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  };

  return (
    <div className="space-y-6 fade-in" data-testid="credit-score-config-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span>System</span><span>/</span><span className="text-[#1f1f1f] font-medium">Credit Score Configuration</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">HEAD OFFICE CONFIGURATION</p>
            <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Credit Score Cut-off Points</h1>
            <p className="text-sm text-gray-500 mt-1">These settings apply to all branches. Configure risk tiers, spreads, and automated lending thresholds.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFormula(true)} data-testid="view-formulas-btn">
            <Settings className="w-4 h-4 mr-2" />Interest Formulas
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="save-config-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>

      {/* Scoring Strategy Selector */}
      <div className="bg-white border border-gray-200 p-6" data-testid="scoring-strategy-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-[#C5A059]" />
            <h3 className="font-semibold text-[#1f1f1f]">Scoring Strategy</h3>
          </div>
          <Button
            onClick={handleRunNow}
            disabled={running}
            className="bg-[#C5A059] hover:bg-[#b38f4e] text-white"
            data-testid="run-credit-scoring-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {running ? 'Running...' : 'Run Credit Scoring Now'}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Select how the system computes each client's credit score each month. The batch job <code className="bg-gray-100 px-1">credit_score_refresh</code> runs on schedule (Admin &gt; System &gt; Manage Jobs).</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            {
              key: 'nkosi_risk_api',
              icon: <ExternalLink className="w-4 h-4" />,
              title: "Nkosi's Risk API",
              desc: 'Calls the custom Nkosi Risk API which aggregates credit bureau data (TransUnion/Experian/XDS — configured in External Services), repayment history and macro signals to return PD/LGD directly. Falls back to Internal Engine on error.',
            },
            {
              key: 'internal',
              icon: <Cpu className="w-4 h-4" />,
              title: 'Internal Engine (FICO-style)',
              desc: 'Compute 300-850 score from local data: Payment 35%, DTI 30%, Age 15%, Mix 10%, New credit 10%.',
            },
            {
              key: 'product_default',
              icon: <Shield className="w-4 h-4" />,
              title: 'Product Portfolio Default',
              desc: 'Skip scoring — ECL engine falls back to admin-table PD/LGD defaults per provisioning criteria. Lowest cost.',
            },
          ].map(opt => {
            const active = (config.evaluation_strategy || 'product_default') === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setConfig({ ...config, evaluation_strategy: opt.key })}
                className={`text-left p-4 border transition-colors ${active ? 'border-[#C5A059] bg-[#FFFDF5]' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                data-testid={`strategy-${opt.key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 flex items-center justify-center ${active ? 'bg-[#C5A059] text-white' : 'bg-gray-100 text-gray-500'}`}>{opt.icon}</div>
                  <span className={`text-sm font-semibold ${active ? 'text-[#1f1f1f]' : 'text-gray-700'}`}>{opt.title}</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{opt.desc}</p>
                {active && <p className="text-[10px] uppercase tracking-wider text-[#C5A059] font-bold mt-2">Active</p>}
              </button>
            );
          })}
        </div>
        {lastRun && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs" data-testid="last-run-summary">
            <p className="font-semibold mb-1">Last Run — Strategy: {lastRun.strategy}</p>
            <p>Processed: <b>{lastRun.clients_processed}</b> clients • Scored: <b>{lastRun.clients_scored}</b> • Loans updated: <b>{lastRun.loans_updated}</b> • Duration: {lastRun.duration_seconds}s</p>
            {lastRun.errors && Object.keys(lastRun.errors).length > 0 && (
              <p className="mt-1 text-amber-800">Warnings: {Object.entries(lastRun.errors).map(([k, v]) => `${k} (${v})`).join(', ')}</p>
            )}
          </div>
        )}
      </div>

      {/* Automated Lending Section */}
      <div className="bg-white border border-gray-200 p-6" data-testid="auto-lending-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#C5A059]" />
            <h3 className="font-semibold text-[#1f1f1f]">Automated Lending Decisions</h3>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={config.auto_decision_enabled || false}
              onCheckedChange={(v) => setConfig({ ...config, auto_decision_enabled: v })}
              data-testid="auto-decision-toggle"
            />
            <span className="text-sm text-gray-600">{config.auto_decision_enabled ? 'Enabled' : 'Disabled'}</span>
          </div>
        </div>
        {config.auto_decision_enabled && (
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">External API URL (future integration)</Label>
              <Input
                value={config.external_api_url || ''}
                onChange={(e) => setConfig({ ...config, external_api_url: e.target.value })}
                placeholder="https://api.creditbureau.co.za/v1/score"
                data-testid="external-api-url"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">API Key</Label>
              <Input
                value={config.external_api_key || ''}
                onChange={(e) => setConfig({ ...config, external_api_key: e.target.value })}
                placeholder="Enter API key..."
                type="password"
                data-testid="external-api-key"
              />
            </div>
            <div className="col-span-2">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">External API Integration Placeholder</p>
                  <p className="mt-1">When an external credit scoring API is integrated, auto-approved tiers will bypass manual review for clients meeting the DTI and score thresholds. Until then, all approvals require manual review.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Tiers */}
      <div className="bg-white border border-gray-200 p-6" data-testid="risk-tiers-section">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-[#C5A059]" />
          <h3 className="font-semibold text-[#1f1f1f]">Risk Tier Configuration</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">Define the credit score bands, interest rate spreads above the reference rate, and DTI limits for each risk tier. Current Reference Rate ({formulas?.rate_index_label || 'Repo Rate'}): <span className="font-bold text-[#1f1f1f]">{formulas?.current_repo_rate || '-'}%</span></p>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="tiers-table">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Tier</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Score Range</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Spread (%)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Effective Rate</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Max DTI (%)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Auto-Approve</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {config.tiers.map((tier, i) => {
                const effectiveRate = (formulas?.current_repo_rate || 0) + (tier.spread || 0);
                return (
                  <tr key={i} className={`border-b ${tierColors[tier.color] || ''}`} data-testid={`tier-row-${i}`}>
                    <td className="px-4 py-3 font-semibold text-sm">{tier.name}</td>
                    <td className="px-4 py-3 text-center text-sm font-mono">{tier.min_score} - {tier.max_score || '850+'}</td>
                    <td className="px-4 py-3 text-center">
                      <Input
                        type="number"
                        step="0.5"
                        value={tier.spread}
                        onChange={(e) => updateTier(i, 'spread', parseFloat(e.target.value) || 0)}
                        className="w-20 text-center mx-auto h-8 text-sm"
                        data-testid={`tier-spread-${i}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-sm">{effectiveRate.toFixed(2)}%</td>
                    <td className="px-4 py-3 text-center">
                      <Input
                        type="number"
                        value={tier.max_dti}
                        onChange={(e) => updateTier(i, 'max_dti', parseInt(e.target.value) || 0)}
                        className="w-20 text-center mx-auto h-8 text-sm"
                        data-testid={`tier-dti-${i}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch
                        checked={tier.auto_approve || false}
                        onCheckedChange={(v) => updateTier(i, 'auto_approve', v)}
                        data-testid={`tier-auto-${i}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditTier({ index: i, ...tier })} data-testid={`edit-tier-${i}`}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* DTI Explanation */}
      <div className="bg-amber-50 border border-amber-200 p-4" data-testid="dti-explanation">
        <h4 className="text-sm font-semibold text-amber-900 mb-2">What is DTI (Debt-to-Income) Limit?</h4>
        <p className="text-xs text-amber-800 leading-relaxed">DTI = Total Monthly Debt Payments / Gross Monthly Income. Per the National Credit Act (NCA), banks must assess affordability before granting credit. The Max DTI % per risk tier sets the ceiling: a client whose total monthly debt exceeds this percentage of their income will be declined.</p>
        <div className="mt-2 grid grid-cols-5 gap-2 text-[10px]">
          <div className="bg-emerald-100 border border-emerald-300 p-2 text-center text-emerald-800"><b>Excellent</b><br/>DTI up to 45%<br/>Lower risk, more capacity</div>
          <div className="bg-emerald-50 border border-emerald-200 p-2 text-center text-emerald-700"><b>Good</b><br/>DTI up to 40%</div>
          <div className="bg-amber-100 border border-amber-300 p-2 text-center text-amber-800"><b>Fair</b><br/>DTI up to 35%</div>
          <div className="bg-orange-100 border border-orange-300 p-2 text-center text-orange-800"><b>Below Average</b><br/>DTI up to 30%</div>
          <div className="bg-red-100 border border-red-300 p-2 text-center text-red-800"><b>Poor</b><br/>DTI up to 25%<br/>High risk, less capacity</div>
        </div>
      </div>

      {/* How it Flows */}
      <div className="bg-white border border-gray-200 p-6">
        <h3 className="font-semibold text-[#1f1f1f] mb-3">How Credit Scoring Works in Loan Decisions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { step: '1', title: 'Score Retrieved', desc: 'Client credit score is fetched (manual or via external API)' },
            { step: '2', title: 'Tier Matched', desc: 'Score is matched to the closest risk tier from this configuration' },
            { step: '3', title: 'Rate Calculated', desc: 'For floating products: Repo Rate + Tier Spread. For fixed: product rate applies.' },
            { step: '4', title: 'Decision Made', desc: 'If auto-decision enabled + tier allows auto-approve + DTI within limit = auto-approved' },
          ].map(s => (
            <div key={s.step} className="flex gap-3 items-start">
              <div className="w-8 h-8 bg-[#1f1f1f] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{s.step}</div>
              <div>
                <p className="font-medium text-sm text-[#1f1f1f]">{s.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Tier Modal */}
      {editTier !== null && (
        <Dialog open={true} onOpenChange={() => setEditTier(null)}>
          <DialogContent aria-describedby="edit-tier-desc">
            <DialogHeader><DialogTitle>Edit Tier: {editTier.name}</DialogTitle></DialogHeader>
            <p id="edit-tier-desc" className="sr-only">Edit credit score tier configuration</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Tier Name</Label><Input value={editTier.name} onChange={(e) => setEditTier({ ...editTier, name: e.target.value })} /></div>
                <div><Label>Color</Label>
                  <select className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm" value={editTier.color} onChange={(e) => setEditTier({ ...editTier, color: e.target.value })}>
                    <option value="emerald">Green (Low Risk)</option>
                    <option value="amber">Amber (Medium Risk)</option>
                    <option value="orange">Orange (High Risk)</option>
                    <option value="red">Red (Very High Risk)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Min Score</Label><Input type="number" value={editTier.min_score} onChange={(e) => setEditTier({ ...editTier, min_score: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Max Score</Label><Input type="number" value={editTier.max_score || 850} onChange={(e) => setEditTier({ ...editTier, max_score: parseInt(e.target.value) || 850 })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Spread above Reference Rate (%)</Label><Input type="number" step="0.5" value={editTier.spread} onChange={(e) => setEditTier({ ...editTier, spread: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Max DTI Ratio (%)</Label><Input type="number" value={editTier.max_dti} onChange={(e) => setEditTier({ ...editTier, max_dti: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editTier.auto_approve || false} onCheckedChange={(v) => setEditTier({ ...editTier, auto_approve: v })} />
                <Label>Auto-Approve (when automated lending is enabled)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTier(null)}>Cancel</Button>
              <Button className="bg-[#1f1f1f] text-white" onClick={() => {
                const { index, ...tierData } = editTier;
                updateTier(index, 'name', tierData.name);
                updateTier(index, 'color', tierData.color);
                updateTier(index, 'min_score', tierData.min_score);
                updateTier(index, 'max_score', tierData.max_score);
                updateTier(index, 'spread', tierData.spread);
                updateTier(index, 'max_dti', tierData.max_dti);
                updateTier(index, 'auto_approve', tierData.auto_approve);
                setEditTier(null);
              }}>Apply</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Interest Formula Dialog */}
      <Dialog open={showFormula} onOpenChange={setShowFormula}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="formula-desc">
          <DialogHeader><DialogTitle>Interest Rate Calculation Formulas</DialogTitle></DialogHeader>
          <p id="formula-desc" className="sr-only">Documentation of interest calculation methods</p>
          {formulas && (
            <div className="space-y-4">
              {Object.entries(formulas.formulas).map(([key, f]) => (
                <div key={key} className="p-4 border border-gray-200">
                  <h4 className="font-semibold text-sm text-[#1f1f1f] mb-2">{f.name}</h4>
                  <p className="text-xs text-gray-600 mb-2">{f.description}</p>
                  <div className="bg-gray-50 p-3 font-mono text-xs space-y-1">
                    <p><span className="text-gray-500">Formula:</span> {f.formula}</p>
                    <p><span className="text-gray-500">Payment:</span> {f.monthly_payment}</p>
                    {f.example && <p className="text-[#C5A059]"><span className="text-gray-500">Example:</span> {f.example}</p>}
                  </div>
                </div>
              ))}
              <div className="p-4 border border-[#C5A059] bg-[#FFFDF5]">
                <h4 className="font-semibold text-sm text-[#1f1f1f] mb-2">Credit Score Spread Table</h4>
                <table className="w-full text-xs">
                  <thead><tr className="border-b"><th className="text-left py-1">Tier</th><th className="text-center py-1">Min Score</th><th className="text-center py-1">Spread</th><th className="text-center py-1">Effective Rate</th></tr></thead>
                  <tbody>
                    {formulas.credit_score_spreads.map(s => (
                      <tr key={s.tier} className="border-b">
                        <td className="py-1 font-medium">{s.tier}</td>
                        <td className="text-center py-1">{s.min_score}</td>
                        <td className="text-center py-1">{s.spread}</td>
                        <td className="text-center py-1 font-bold">{s.effective_rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditScoreConfigPage;
